<script setup>
import { computed, onMounted, ref, watch } from "vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { vehicleStore } from "@/_store/vehicleStore";
import ModalVehicle from "./ModalVehicle.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import VehicleAvatar from "@/components/VehicleAvatar.vue";
import CopyValue from "@/components/CopyValue.vue";
import ConfirmModal from "@/components/modal/ConfirmModal.vue";
import { useConfirmModal } from "@/composables/useConfirmModal";
import { userStore } from "@/_store/userStore";
import { errorUtils, fileUtils } from "@/utils";

const showModal = ref(false);
const selectedVehicleId = ref(null);
const tableStore = argonTableStore();
const store = vehicleStore();
const userStores = userStore();
const snackbar = ref(null);
const {
  showConfirmModal,
  confirmTitle,
  confirmText,
  confirmButtonText,
  cancelButtonText,
  confirmButtonClass,
  cancelButtonClass,
  confirmLoading,
  openConfirmModal,
  handleConfirmModal,
} = useConfirmModal();
const fieldFilters = ref([
  {
    field: "name",
    description: "Alias",
  },
  {
    field: "registration_number",
    description: "Matrícula",
  },
  {
    field: "model",
    description: "Modelo",
  },
]);
const defaultFilters = ref([
  {
    field: "active",
    label: "Estado",
    multi: false,
    options: [
      { label: "Activo", value: true },
      { label: "Inactivo", value: false },
    ],
  },
]);
const tableItems = computed(() => store.vehicles);
const vehicleRows = computed(() => {
  if (Array.isArray(store.vehicles?.data)) return store.vehicles.data;
  if (Array.isArray(store.vehicles?.vehicles)) return store.vehicles.vehicles;
  if (Array.isArray(store.vehicles)) return store.vehicles;
  return [];
});
const selectedVehicles = computed(() =>
  vehicleRows.value.filter((vehicle) =>
    tableStore.checkedList.includes(vehicle._id)
  )
);
const canDeleteSelected = computed(() => selectedVehicles.value.length > 0);
const columns = ref([
  { header: "Alias", field: "name", sort: false, position: "start" },
  {
    header: "Matrícula",
    field: "registration_number",
    sort: false,
    position: "start",
  },
  { header: "Capacidad", field: "capacity", sort: false, position: "start" },
  { header: "Horario", field: "time_window", sort: false, position: "start" },
  {
    header: "Días laborables",
    field: "working_days",
    sort: false,
    position: "start",
  },
  {
    header: "Estado",
    field: "active",
    sort: false,
    position: "center",
    width: "w-5",
  },
  { header: "", field: "actions", sort: false, position: "center", width: "w-1" },
]);

onMounted(() => {
  tableStore.clear();
  const savedFilters = userStores.filtersPage.vehicles?.filters || [];
  tableStore.changeFilters(savedFilters);
  reloadPage();
});

function reloadPage() {
  userStores.filtersPage.vehicles = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };
  store.getVehicles(tableStore.pagination, tableStore.query, tableStore.sort);
}

async function exportVehicles() {
  const bytes = await store.exportar(
    tableStore.pagination,
    tableStore.query,
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function openNewVehicleModal() {
  selectedVehicleId.value = null;
  showModal.value = true;
}

function editVehicle(vehicle) {
  selectedVehicleId.value = vehicle?._id || null;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function successSaveVehicle() {
  showModal.value = false;
  reloadPage();
}

function formatDays(days) {
  if (!Array.isArray(days) || !days.length) return "-";
  const map = {
    0: "L",
    1: "M",
    2: "X",
    3: "J",
    4: "V",
    5: "S",
    6: "D",
    monday: "L",
    tuesday: "M",
    wednesday: "X",
    thursday: "J",
    friday: "V",
    saturday: "S",
    sunday: "D",
  };
  return days
    .map((d) => map[d] || map[String(d).toLowerCase()] || String(d))
    .join(" · ");
}
function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column?.position || "start";
}

const usedVehicleColors = computed(() => {
  const selectedId = selectedVehicleId.value || null;
  return vehicleRows.value
    .filter((vehicle) => vehicle._id !== selectedId)
    .map((vehicle) =>
      String(vehicle.route_color_hex || "")
        .trim()
        .toUpperCase()
    )
    .filter((color) => /^#[0-9A-F]{6}$/.test(color));
});

async function deleteSelectedVehicles() {
  if (!canDeleteSelected.value) return;

  openConfirmModal({
    title: "¿Está seguro de querer eliminar los vehículos seleccionados?",
    text: "¡No podrá revertir esta acción!",
    confirmButtonText: "Eliminar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-danger",
    onConfirm: () => {
      const selectedIds = [...new Set(tableStore.checkedList)];
      const idsAsCsv = selectedIds.join(",");
      if (!idsAsCsv) return false;

      store.deleteVehicle(idsAsCsv);
    },
  });
}

function deleteVehicleRow(vehicle) {
  const vehicleId = vehicle?._id;
  if (!vehicleId) return;

  openConfirmModal({
    title: "¿Está seguro de querer eliminar este vehículo?",
    text: "¡No podrá revertir esta acción!",
    confirmButtonText: "Eliminar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-danger",
    onConfirm: () => {
      store.deleteVehicle(String(vehicleId));
    },
  });
}

function formatTimeWindow(timeWindow) {
  const fromTime = timeWindow?.start_time || timeWindow?.from_time || "--";
  const toTime = timeWindow?.end_time || timeWindow?.to_time || "--";
  return `${fromTime} - ${toTime}`;
}

const closeSnackbar = () => {
  snackbar.value = null;
};

function getRowActionsMenuId(vehicle) {
  return `vehicle-row-actions-${String(vehicle?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions() {
  return [
    [{ label: "Editar", event: "edit" }],
    [{ label: "Eliminar", event: "delete", type: "danger" }],
  ];
}

function onRowAction(vehicle, action) {
  const actionEvent = action?.event;
  if (!actionEvent || !vehicle) return;

  if (actionEvent === "edit") {
    editVehicle(vehicle);
    return;
  }

  if (actionEvent === "delete") {
    deleteVehicleRow(vehicle);
  }
}

watch(
  () => store.vehicleDeleted,
  (value) => {
    if (value) {
      tableStore.clearSelection();
      reloadPage();
      snackbar.value = {
        title: "OK",
        message: "Vehículo eliminado",
        type: "info",
        color: "white",
      };
    }
  }
);

watch(
  () => store.error,
  (value) => {
    if (!value) return;
    if (showModal.value) return;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);
</script>

<template>
  <div class="card vehicles-table-view">
    <div class="card-body vehicles-table-body">
      <argon-table
        v-if="store.vehicles"
        :columns="columns"
        :items="tableItems"
        :field-filters="fieldFilters"
        :default-filters="defaultFilters"
        :show-config-option="true"
        :show-importar="false"
        label-button="Nuevo"
        @filter-changed="reloadPage"
        @on-reload="reloadPage"
        @sort-changed="onSortChanged"
        @page-changed="reloadPage"
        @new-item="openNewVehicleModal"
        @on-click="editVehicle"
        @export-data="exportVehicles"
      >
        <template #buttons>
          <div
            class="dropdown d-inline"
            data-dropdown-static
            style="--dropdown-gap: 4px"
          >
            <argon-button
              id="vehicleActionsDropdown"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              <i class="fa fa-exchange me-sm-2"></i>Acciones
            </argon-button>
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="vehicleActionsDropdown"
            >
              <li>
                <a
                  class="dropdown-item border-radius-md text-danger"
                  :class="{ disabled: !canDeleteSelected }"
                  @click.prevent="deleteSelectedVehicles"
                >
                  Eliminar
                </a>
              </li>
            </ul>
          </div>
        </template>
        <template #default="{ item }">
          <argon-td
            :position="getPosition('name')"
            :text-primary="item.name"
            :text-secondary="item.model"
          >
            <template #prepend>
              <vehicle-avatar
                :label="item.name"
                :abbreviation="item.abbreviation"
                :accent-color="item.route_color_hex"
                :size="28"
              />
            </template>
          </argon-td>
          <argon-td
            :position="getPosition('registration_number')"
            :text-primary="item.registration_number"
          >
            <template v-if="item.registration_number" #append>
              <copy-value :value-to-copy="String(item.registration_number)" size="xs" show-on-hover />
            </template>
          </argon-td>
          <argon-td
            :position="getPosition('capacity')"
            :text-primary="`${item.max_weight} kg`"
            :text-secondary="`${item.max_volume} m3`"
          />
          <argon-td
            :position="getPosition('time_window')"
            :text-primary="formatTimeWindow(item.time_window)"
          />
          <argon-td
            :position="getPosition('working_days')"
            :text-primary="formatDays(item.working_days)"
          />
          <argon-td :position="getPosition('active')">
            <argon-status
              entity="vehicle"
              :status="item.active ? 'ACTIVO' : 'INACTIVO'"
              width="80px"
            />
          </argon-td>
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions()"
                aria-label="Acciones del vehículo"
                trigger-class="table-action-button"
                menu-class="table-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
    </div>
  </div>

  <modal-vehicle
    v-if="showModal"
    :vehicle-id="selectedVehicleId"
    :used-colors="usedVehicleColors"
    @close="closeModal"
    @success="successSaveVehicle"
  />
  <confirm-modal
    v-if="showConfirmModal"
    v-model="showConfirmModal"
    :title="confirmTitle"
    :text="confirmText"
    :confirm-button-text="confirmButtonText"
    :cancel-button-text="cancelButtonText"
    :confirm-button-class="confirmButtonClass"
    :cancel-button-class="cancelButtonClass"
    :loading="confirmLoading"
    @confirm="handleConfirmModal"
  />

  <div class="position-fixed top-0 end-0 z-index-2 p-3">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped>
.z-index-1030 {
  z-index: 1030 !important;
}


:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}
</style>
